/**
 * Helper for parent detail pages that host section-managed sections.
 * Bundles dirty state + save handler ref to avoid useState + useRef per section.
 *
 * Section-managed: Section owns its mutations, exposes save via ref (e.g. content references).
 * Parent-managed: Section reports changes up, parent runs mutation (e.g. title, description).
 *
 * Usage: const refs = useSectionManagedSlot()
 * Pass refs.onDirtyChange and refs.saveHandlerRef to section.
 * On save: if (refs.dirty && refs.saveHandlerRef.current) await refs.saveHandlerRef.current()
 */

import { useState, useRef } from 'react'

export interface UseSectionManagedSlotResult {
  dirty: boolean
  onDirtyChange: (isDirty: boolean) => void
  saveHandlerRef: React.MutableRefObject<(() => Promise<void>) | null>
}

/**
 * Returns dirty flag, setter for section to report dirty, and ref for section to assign its save handler.
 * Parent uses: isDirty = a.dirty || b.dirty; on Save: if (a.dirty && a.saveHandlerRef.current) await a.saveHandlerRef.current()
 */
export function useSectionManagedSlot (): UseSectionManagedSlotResult {
  const [dirty, setDirty] = useState(false)
  const saveHandlerRef = useRef<(() => Promise<void>) | null>(null)
  return {
    dirty,
    onDirtyChange: setDirty,
    saveHandlerRef,
  }
}
